/**
 * 账户契约 —— 建 / 改 / 删 / 排序 / 改归属人 · 视图（**B 域**）
 *
 * 依据：B 域 PRD **§5.1**（账户管理）· **§5.2**（信用卡最小集）· **§5.4**（冷启动）·
 * 规格 **§1 / §8 / §22.6** · `DR-004`（多币种）· `DR-008`（账户体系）· `DR-021` §6.1~§6.3
 * （8 类 · `ownerId` NOT NULL · 改归属人仅 owner）· 架构 **§4.2**（`category` 与 `contraKind` 互斥）·
 * **§6.1**（契约唯一源）· **§6.5**（写接口幂等）。
 *
 * ## 🔴 三条"契约层就该堵死"的东西
 *
 * 1. **`type` 不可改** —— 建/改请求里**没有** `type` 的更新入口。
 *    一张储蓄卡不会"变成"信用卡；真要换形态就删了重建。允许改 `type` 会让
 *    「信用卡四字段只允许出现在 credit 上」「`category` 与 `contraKind` 互斥」
 *    这两条形态规则在更新路径上再判一次（= 同一规则两处实现）。
 * 2. 🔴 **`PATCH /api/accounts/:id` **刻意不收** `ownerId`** —— 改归属人是
 *    规格 §16 唯一那项"仅 owner"的例外（`PERMISSION_OWNER_ONLY_EXTRA`），
 *    而 `@RequireOwner()` 是**按路由**挂的。让 PATCH 接收 `ownerId`，就等于
 *    让 member 用普通编辑接口绕过白名单。⇒ 归属人只能经专用路由改（`PUT .../owner`）。
 * 3. **金额一律整数分**，且账户级**单币种**（余额永不折算、不跨币种相加，`DR-004`）。
 *    腿的币种继承账户 ⇒ 账户币种是"这个数字是什么钱"的唯一定义处。
 */

import { z } from 'zod';
import { AccountPolicy } from '../constants/index.js';
import { AccountType, ContraKind, InternalCategory, LoanSubtype, RepaymentMethod } from '../enums/account.js';
import type { CreditBill, CreditStanding, CreditUnbilled, CreditUsageLevel } from '../nucleus/account.js';
import type { BillLegChildView, BillLegRowView } from '../nucleus/bill-rows.js';
import type { RatioE4 } from '../types/brand.js';

// ═══════════════════════ 取值集（8 类 / 13 品类 / 5 类） ═══════════════════════

/**
 * 账户类型 —— 8 类（`AccountType` 是**唯一来源**，此处只是把它翻成 Zod 枚举）。
 * 🔴 v1 的 `prepaid`（预付储值）**已取消**，语义并入 `internal` 的 `prepaid` 品类。
 */
export const AccountTypeSchema = z.enum([
  AccountType.Cash,
  AccountType.Debit,
  AccountType.Ewallet,
  AccountType.Credit,
  AccountType.Loan,
  AccountType.Investment,
  AccountType.Internal,
  AccountType.Contra,
]);

/** `internal` 品类 —— 13 个（架构 §4.3） */
export const InternalCategorySchema = z.enum([
  InternalCategory.Payable,
  InternalCategory.Prepaid,
  InternalCategory.Amortize,
  InternalCategory.InsuranceSav,
  InternalCategory.SocialPension,
  InternalCategory.SocialPensionSup,
  InternalCategory.SocialMedical,
  InternalCategory.SocialUnemploy,
  InternalCategory.SocialFund,
  InternalCategory.SocialFundSup,
  InternalCategory.SocialMedicalCur,
  InternalCategory.SocialMedicalHist,
  InternalCategory.SocialFundVirtual,
]);

/** `contra` 事项类型 —— 5 类（`DR-022`） */
export const ContraKindSchema = z.enum([
  ContraKind.Receivable,
  ContraKind.Payable,
  ContraKind.Advance,
  ContraKind.Reimburse,
  ContraKind.Loan,
]);

/** 🆕 `loan` 还款方式 —— 2 种（`DR-044` D4） */
export const RepaymentMethodSchema = z.enum([
  RepaymentMethod.EqualInstallment,
  RepaymentMethod.EqualPrincipal,
]);

/** 🆕 `loan` 子类型 —— 4 种（`DR-044` D5） */
export const LoanSubtypeSchema = z.enum([
  LoanSubtype.Mortgage,
  LoanSubtype.Auto,
  LoanSubtype.Consumer,
  LoanSubtype.Other,
]);

// ═══════════════════════════════ 入参 ═══════════════════════════════

/** 账户 id（**形状**）：统一 `acc_` 前缀 —— 前缀在 `IdPrefix.Account` 登记 */
const AccountIdSchema = z.string().trim().min(1, '账户 id 不得为空');

/** 归属人 id（`users.id`）—— 是否属于本账本由服务端校验（契约层看不到成员表） */
const OwnerIdSchema = z.string().trim().min(1, '归属人不得为空');

/**
 * 币种 —— ISO 4217 三字母。
 * 🔴 P0 **UI 固定 CNY**（B 域 PRD §3.2），但**结构支持多币种**（`DR-004` 已进 P0）
 * ⇒ 接口放开，端侧按 A2 的解锁节奏决定露不露。
 */
export const CurrencySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/, '币种须为 ISO 4217 三字母代码（如 CNY / USD）')
  .transform((s) => s.toUpperCase());

/** 金额（整数分，可负 —— 初始余额允许为负，如"这张卡现在就欠着"） */
const CentsSchema = z
  .number({ message: '金额必须是数字（整数「分」）' })
  .int('金额必须是整数「分」（规格 §22.1 全链路整数分）')
  .min(-AccountPolicy.InitialBalanceAbsLimitCents, `初始余额绝对值不得超过 ${AccountPolicy.InitialBalanceAbsLimitCents} 分`)
  .max(AccountPolicy.InitialBalanceAbsLimitCents, `初始余额绝对值不得超过 ${AccountPolicy.InitialBalanceAbsLimitCents} 分`);

/** 账户名 */
const NameSchema = z
  .string()
  .trim()
  .min(1, '账户名不得为空')
  .max(AccountPolicy.NameMaxLength, `账户名不超过 ${AccountPolicy.NameMaxLength} 个字符`);

/**
 * 🆕 预付卡次卡两项（`DR-057` D4）—— 仅 `internal(prepaid)` 有语义。
 *
 * ⚠️ 契约层只做**形状**（整数、正数）；「两项必须成对」「不能小于已用次数」「仅预付卡」
 * 全在内核 `checkAccountShape()` 规则 13 —— 那里一处判据同时覆盖新建与编辑两个入口。
 */
const PassTimesFields = {
  passTotalTimes: z.number().int('总次数必须是整数').positive('总次数必须大于 0').nullish(),
  passUnitAmount: z.number().int('单次金额必须是整数分').positive('单次金额必须大于 0').nullish(),
};

/** 信用卡四字段（**只在 `type='credit'` 上出现** —— 由 `checkAccountShape` 兜底校验） */
const CreditFields = {
  /** 整数分，> 0 */
  creditLimit: z.number().int('信用额度必须是整数分').positive('信用额度必须大于 0').nullish(),
  /** 账单日 / 还款日：`[1, 31]`（🔴 见 `AccountPolicy` 的说明：31 在短月按「月末」处理，与花呗/白条语义一致） */
  billingDay: z
    .number()
    .int()
    .min(AccountPolicy.BillingDayMin, `账单日不小于 ${AccountPolicy.BillingDayMin}`)
    .max(AccountPolicy.BillingDayMax, `账单日不大于 ${AccountPolicy.BillingDayMax}`)
    .nullish(),
  repaymentDay: z
    .number()
    .int()
    .min(AccountPolicy.BillingDayMin, `还款日不小于 ${AccountPolicy.BillingDayMin}`)
    .max(AccountPolicy.BillingDayMax, `还款日不大于 ${AccountPolicy.BillingDayMax}`)
    .nullish(),
  /** 卡号后四位（**展示用**；🔴 不存完整卡号） */
  tailNumber: z.string().trim().max(4, '只保留卡号后四位').nullish(),
} as const;

/**
 * 🆕 贷款参数六字段（**只在 `type='contra'` 且 `contraKind='loan'` 上出现**
 * —— 由 `checkAccountShape()` 兜底校验）。
 *
 * 依据：**`DR-044`**（2026-10-16 拍板）· `DR-022` §2.1 · `借贷域口径对齐单` §2.1 ·
 * `F域PRD` §4.5（`loan` 计划表按本金 / 利率 / 期数生成）· `Q域PRD` §5（「贷款再填利率期数」）。
 *
 * ## 🔴 三条纪律
 *
 * 1. **六个字段全部可选 / 可空**（`DR-044` §5 第 2 项拍板）—— 用户常只知道"房贷还剩 200 期"
 *    而不知精确利率。**缺失就是缺失**，`null` 不表示 0。
 * 2. **`annualRateE4` 是万分之一整数**（`336` = 3.36%），沿用全库 `E4` 命名与标度
 *    （`DR-023` D3）；🔴 与 v1 的 `annual_rate_bps` **数值完全相同**、无需换算。
 * 3. **`terms` 是总期数**（`DR-044` §5 第 3 项拍板）；剩余 = `terms − 已还期次`，
 *    由 `loan_schedules` 数出，**不落库**（架构 §4.1 派生铁律）。
 */
const ContraTermFields = {
  /**
   * 到期日（账本本地日历日 `YYYY-MM-DD`）。
   * 无固定期限（如对人借款）可留空 —— `AC-Q4.5` 卡片显示到期日时留空即不显示。
   */
  dueDate: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '到期日格式必须是 YYYY-MM-DD（账本本地日历日）')
    .nullish(),
  /**
   * **年利率**（万分之一整数，`E4` 标度）：`336` = 3.36%。
   * 🔴 非负；上限 `AccountPolicy.AnnualRateE4Max`（= 100%）只拦"万分之一当百分数填"。
   */
  annualRateE4: z
    .number({ message: '年利率必须是数字（万分之一整数）' })
    .int('年利率必须是整数（万分之一标度：336 = 3.36%）')
    .min(0, '年利率不得为负')
    .max(AccountPolicy.AnnualRateE4Max, `年利率不得超过 ${AccountPolicy.AnnualRateE4Max}（万分之一标度，= 100%）`)
    .nullish(),
  /** **总期数**（非剩余期数）；`termsMax` 只拦"多打几个 0" */
  terms: z
    .number()
    .int('期数必须是整数')
    .min(1, '期数至少为 1（留空表示未知，不要填 0）')
    .max(AccountPolicy.TermsMax, `期数不得超过 ${AccountPolicy.TermsMax}（= 100 年 × 12 期）`)
    .nullish(),
  /** **起息日**（账本本地日历日 `YYYY-MM-DD`） */
  startDate: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '起息日格式必须是 YYYY-MM-DD（账本本地日历日）')
    .nullish(),
  /** 还款方式（2 种，`DR-044` D4） */
  method: RepaymentMethodSchema.nullish(),
  /** 贷款子类型（4 种，`DR-044` D5） */
  subtype: LoanSubtypeSchema.nullish(),
} as const;

/**
 * 🆕 对手方（**只在 `type='contra'` 上有语义** —— 由 `checkAccountShape()` 规则 11 兜底）。
 *
 * 依据：**`DR-046`**（2026-10-17 拍板）· `DR-022` §2.1（「`counterparty`（跟谁）」）·
 * `Q域PRD` `AC-Q4.5`（「副行对手方 + 归属人」）。
 *
 * 🔴 **为什么不放进 `ContraTermFields`**：那个块是「**期限**」（到期日 / 利率 / 期数…），
 * 只在 `contra + loan` 或 `contra` 上有意义；而对手方是**身份**（跟谁），与期限无关。
 * 混进同一个块会让"期限"这个词承载两种归属层级（`DR-046` D2 的三层归属）。
 *
 * 🔴 **刻意不用 `.transform()` 做「空串 → null」归一**（2026-10-17 实测拦下的一处）：
 * `.transform()` 会把**没传的键也补成 `null`** ⇒ `PATCH { name }` 解析成
 * `{ name, counterparty: null }` = **静默清空对手方**（既有契约测试当场变红）。
 * ⇒ 归一位置下沉到**服务端**（`account.service.ts`），契约层只做「长度 + 空串放行」；
 * **"没传"必须保持"没这个键"**（铁律 63：可选键按需剔键）。
 */
const CounterpartyField = {
  counterparty: z
    .string()
    .trim()
    .max(
      AccountPolicy.CounterpartyMaxLength,
      `对手方不超过 ${String(AccountPolicy.CounterpartyMaxLength)} 个字符`,
    )
    .nullish(),
} as const;

/**
 * `POST /api/accounts` —— 建账户。
 *
 * ⚠️ **形态规则（`category` / `contraKind` 互斥、信用卡字段归属、账单日成对）契约层表达不了**
 * —— 它们要求先知道 `type`，而 `zod` 的 `superRefine` 只能给出条文式文案，
 * 与内核 `checkAccountShape()` 的**同一条规则**会长出第二份实现。
 * ⇒ 契约层只做**逐字段**校验（类型 / 区间 / 长度），**形态规则统一由内核函数判定**
 * （服务端 `AccountService` 调用；两端表单也用同一函数做即时提示）。
 */
export const CreateAccountRequestSchema = z.object({
  name: NameSchema,
  type: AccountTypeSchema,
  /** 仅 `type='internal'` 时用（13 品类之一） */
  category: InternalCategorySchema.nullish(),
  /** 仅 `type='contra'` 时用（5 类之一） */
  contraKind: ContraKindSchema.nullish(),
  /** 缺省 = 当前登录用户（B 域 PRD §5.1.5：★ 建账户表单必含「归属人」，默认当前用户） */
  ownerId: OwnerIdSchema.nullish(),
  /** 缺省 `CNY` */
  currency: CurrencySchema.optional(),
  /** 缺省 0 */
  initialBalance: CentsSchema.optional(),
  icon: z.string().trim().min(1).nullish(),
  /** 展示顺序；缺省 = 追加到末尾（服务端取当前最大值 + 1） */
  sort: z.number().int().min(0).optional(),
  hidden: z.boolean().optional(),
  ...PassTimesFields,
  ...CreditFields,
  ...ContraTermFields,
  ...CounterpartyField,
});
export type CreateAccountRequest = z.infer<typeof CreateAccountRequestSchema>;

/**
 * `PATCH /api/accounts/:id` —— 改账户。
 *
 * 🔴 **不含三样东西，每一样都对应一条铁律**：
 *
 * | 不接收 | 理由 |
 * |---|---|
 * | `type` | 一张储蓄卡不会"变成"信用卡；允许改会让形态规则在更新路径上再判一次（见文件头第 1 条） |
 * | `ownerId` | 改归属人是**仅 owner** 的例外，而 `@RequireOwner()` 按**路由**挂 ⇒ 只能走专用路由（第 2 条） |
 * | `initialBalance` | 🔴 **`AC-B6.7` 红线：无「直接改账户余额」路径**（B 域 PRD §3.2 亦把「账户余额手工调整（不走流水）」列入**明确排除**）。初始余额是**录入期的事实值**、只在建账户时写一次；事后校准走 `POST /api/accounts/:id/adjust-balance`（`DR-056`，批 96）—— 它**仍然生成一笔调整腿**，只是可选地记一个锚点使余额锁定。允许 PATCH 它就是给红线开了一个后门 |
 *
 * 字段语义 = **传了就替换**（与流水编辑同约定）；`null` 表示"清空该字段"。
 */
export const UpdateAccountRequestSchema = z.object({
  name: NameSchema.optional(),
  category: InternalCategorySchema.nullish(),
  contraKind: ContraKindSchema.nullish(),
  currency: CurrencySchema.optional(),
  icon: z.string().trim().min(1).nullish(),
  sort: z.number().int().min(0).optional(),
  hidden: z.boolean().optional(),
  ...PassTimesFields,
  ...CreditFields,
  ...ContraTermFields,
  ...CounterpartyField,
});
export type UpdateAccountRequest = z.infer<typeof UpdateAccountRequestSchema>;

/**
 * `PUT /api/accounts/:id/owner` —— 改归属人。
 *
 * 🔴 **本接口是规格 §16 唯一那项"仅 owner"的例外**（`change_account_owner`）⇒
 * 路由上必须挂 `@RequireOwner(PERMISSION_OWNER_ONLY_EXTRA[0])`。
 * 目标人**必须是本账本的活跃成员**（服务端校验）—— 否则账本里会出现一个
 * "钱属于某人，但那人不在这个家"的孤儿归属。
 */
export const SetAccountOwnerRequestSchema = z.object({ ownerId: OwnerIdSchema });
export type SetAccountOwnerRequest = z.infer<typeof SetAccountOwnerRequestSchema>;

/**
 * `PUT /api/accounts/order` —— 拖拽排序。
 *
 * 🔴 **整体提交而不是"单条改 sort"**：拖拽一回会连带改动多条的顺序，
 * 逐条 PATCH 会留下"只改了一半"的中间态（网络中断即错序）。
 * 服务端按数组下标重写 `sort`，**同一事务**。
 *
 * ⚠️ 必须覆盖**本账本全部账户**（缺一个 ⇒ 400），否则未列出的账户会保留旧 `sort`，
 * 与新的 0..n-1 序列交错，排序结果变成"看似随机"。
 */
export const ReorderAccountsRequestSchema = z.object({
  orderedIds: z
    .array(AccountIdSchema)
    .min(1, '至少要有一个账户')
    .max(AccountPolicy.MaxAccountsPerLedger, `单账本账户数上限 ${AccountPolicy.MaxAccountsPerLedger}`),
});
export type ReorderAccountsRequest = z.infer<typeof ReorderAccountsRequestSchema>;

/**
 * `DELETE /api/accounts/:id` 的查询参数。
 *
 * 🔴 **`confirmNonZeroBalance` 的语义是"我知道这笔余额会被直接清零"**（B 域 PRD §6.1 **E7**：
 * 「删除有余额的内部账户 ⇒ **需确认** + 显示余额处置；**禁止直接清零**」）。
 * ⇒ 不带它删一个余额非零的账户会被 **409** 拦下，并把余额与币种放进 `details`，
 * 由前端展示「将清零 ¥X（原币 Y）」后再让用户确认。
 *
 * ⚠️ 用 `z.enum(['true','false'])` 而**不是** `z.coerce.boolean()`：后者会把字符串
 * `"false"` 也强转成 `true`（非空字符串恒真）—— 那等于确认开关**永远处于开着的状态**。
 */
export const DeleteAccountQuerySchema = z.object({
  confirmNonZeroBalance: z.enum(['true', 'false']).optional(),
});
export type DeleteAccountQuery = z.infer<typeof DeleteAccountQuerySchema>;

// ═══════════════════════════════ 视图 ═══════════════════════════════

/**
 * 🆕 卡面「分期未还」块（`DR-060` §4.3 · `AC-B2.24`）。
 *
 * 🔴 **与「本期应还」「未出账单」三块并存、不合并**：「分期未还」是**负债**
 * （Σ pending 期次本金，不含手续费），**不计入本期应还** —— 端侧必须并排展示
 * 并标注「不计入本期应还」（防误读为"要还两笔"）。
 */
export interface CreditInstallmentSummary {
  /** 分期未还本金（分；**不含手续费**，`DR-060` D3） */
  remainingPrincipal: number;
  /** 剩余期数（pending 计数） */
  remainingPeriods: number;
  /** 预计结清账单日 `YYYY-MM-DD`（最晚期次到期日） */
  estimatedEndBill: string | null;
}

/** 信用卡专属视图块（`type='credit'` 时非空） */
export interface CreditCardView {
  creditLimit: number | null;
  billingDay: number | null;
  repaymentDay: number | null;
  tailNumber: string | null;
  /** 使用率（万分之一整数）；🔴 额度未填 / 余额非负 ⇒ `null`（进度条**不显示**，`AC-B2.8`） */
  usageE4: RatioE4 | null;
  /** 使用率档位（常规 / 琥珀 / 朱红）；`null` = 不显示进度条 */
  usageLevel: CreditUsageLevel | null;
  /**
   * 余额档位（应还 / 临近 / 逾期 / 溢缴 / 结清）。
   * 🔴 **「应还」绝不用支出绿**（`AC-B2.6` 红线）—— 端侧按本字段选色，不得自行判断。
   */
  standing: CreditStanding;
  /** 当期账单；账单日与还款日**成对**配置时才非空（否则无从算账期） */
  bill: CreditBill | null;
  /** 🆕 分期摘要；无分期 / 已全部结清 / 未配账期 ⇒ `null`（卡面隐藏「分期未还」块） */
  installment: CreditInstallmentSummary | null;
}

/**
 * 🆕 **一张信用卡的多期账单**（批次 `94` · `GET /api/accounts/credit-bills` 的单项）。
 *
 * ## 🔴 为什么是"批量接口"而不是"每张卡一个"
 *
 * 负责人 2026-10-21 的 dev 账本里有 **12 张卡**（招行 / 平安 / 花呗 / 白条 / 月付…）。
 * 逐卡请求 = 12 次往返，且每次都要重算一次账期窗口 ⇒ N+1（架构 §6.4「服务端单接口下发」）。
 *
 * ## 🔴 为什么数字必须由服务端给（端侧不许自己算）
 *
 * 账期窗口是**半开区间 + 月末 clamp**（账单日 31 在 2 月按月末）。端侧重算一次
 * 就会得出"月末那天算哪一期"的另一个答案 —— 而这是**与银行对账单对不上**的直接原因
 * （B 域 PRD §5.2.2 的产品意义）。⇒ 口径唯一实现在 `nucleus/account.ts`，
 * 本视图只负责搬运。
 */
export interface CreditBillsView {
  accountId: string;
  name: string;
  currency: string;
  tailNumber: string | null;
  creditLimit: number | null;
  billingDay: number | null;
  repaymentDay: number | null;
  /**
   * **剩余可用额度**（原币整数分）= `creditLimit + balance`（余额为负 = 已用）。
   * 🔴 额度未填 ⇒ `null`（不显示，而不是显示 0 —— 0 会被读成"额度用光了"）。
   */
  availableCredit: number | null;
  /** 使用率（万分之一）；与 `CreditCardView.usageE4` 同一口径 */
  usageE4: RatioE4 | null;
  usageLevel: CreditUsageLevel | null;
  standing: CreditStanding;
  /** 当前余额（欠款为负） */
  balance: number;
  /** 当期账单（未配账期 ⇒ `null`） */
  current: CreditBill | null;
  /** 未出账单（本期账单日 → 今天）；未配账期 ⇒ `null` */
  unbilled: CreditUnbilled | null;
  /** 往期账单，**时间倒序**（`history[0]` = 上一期） */
  history: CreditBill[];
  /** 🆕 分期摘要（卡面第三块 · `AC-B2.24`）；无分期 / 已全部结清 ⇒ `null` */
  installment: CreditInstallmentSummary | null;
}

/** 批量结果（不分页 —— 一个账本的信用卡数量是个位数到十几个） */
export interface CreditBillsResult {
  items: CreditBillsView[];
}

/**
 * 🆕 **一个账户的流水明细行**（批次 `95` · `GET /api/accounts/:id/ledger`）。
 *
 * ## 🔴 为什么要一个"通用账户明细"接口
 *
 * 往来账户有 `GET /api/contra/:id/timeline`，但那个带**结算行**语义（本金的唯一痕迹），
 * 套不到预付 / 待摊 / 信用卡上。而"这个账户的钱怎么动过"是所有账户都需要的能力 ——
 * 此前只有往来账户能看，其余账户点进去只有余额（负责人 2026-10-22：「待摊和预付没有明细显示」）。
 */
export interface AccountLegRowView {
  /** 所属交易（点进去可开流水抽屉） */
  transactionId: string;
  /** 账本本地日历日 `YYYY-MM-DD` */
  date: string;
  direction: 'in' | 'out';
  /**
   * 该腿金额 —— 🆕 批 `106-A` 起是**合并后**的总额：
   * 同一交易落在该账户且**同向**的腿 ≥ 2（拆分）⇒ 合成一行、金额 = Σ（`AC-B2.64`）。
   * 未合并时 `legCount = 1`，金额 = 那条腿。
   */
  amount: number;
  categoryId: string | null;
  /**
   * 🆕 分类中文名（服务端 JOIN 下发，批 `106-A`）。
   * 🔴 端侧**不得**按 `categoryId` 自己 mapping（铁律 18 / 103：分类名的来源只有一处）。
   * 全部子腿分类不同（拆分）⇒ `null`。
   */
  label: string | null;
  note: string | null;
  /** 交易级备注（腿级为空时展示） */
  txnNote: string | null;
  /** 🆕 合并的腿数（`1` = 未合并）—— 端侧据此显示「拆分 N 项」并决定是否可展开 */
  legCount: number;
  /** 🆕 子项（**仅合并行出发此键**）；展开**不改变任何合计**（`AC-B2.39`） */
  children?: BillLegChildView[];
}

/**
 * 🆕 **某一期账单的对账块**（批 `106-A` · `GET /api/accounts/credit-bills/legs` 的 `summary`）。
 *
 * ## 🔴 为什么明细必须带对账块（`AC-B2.33`）
 *
 * 「本期应还 ≠ 消费 − 退款」—— 分期调整与还款优惠都会改变应还。
 * 不把这几项摆出来，用户拿着明细逐行加，加不出「本期应还」那个数，
 * 而对不上银行账单正是 B 域 PRD §5.2.2 列的**直接伤害**（用户按家盈的少还 ⇒ 循环利息）。
 *
 * ⇒ 本结构的每一项都与 `CreditBill` / `CreditUnbilled` **同名字段对齐**，
 * 差异只在「算不出的项不出键」（可选键剔键纪律，见 `.transform()` 那条踩坑）。
 */
export interface CreditBillSummaryView {
  /** 上期余额（当期 / 往期有；未出账单**不出键** —— `DR-064` D5） */
  openingBalance?: number;
  /** 本期流出（窗口内 `out` 腿合计；同 `consumed` 的另一个名字） */
  outflow?: number;
  /** 本期流入（退款 + 还款；不含优惠腿 / 分期本金腿 —— `DR-064` D1） */
  inflow?: number;
  /** 流入里的退款部分（`inflowRefund`） */
  inflowRefund?: number;
  /** 流入里的还款部分（`inflowRepayment`） */
  inflowRepayment?: number;
  /**
   * 🆕 未出账单的**净额** = `outflow − inflow`（服务端算，端侧零算术 —— `AC-B2.70`）。
   * 🔴 只有未出账单出这个键；当期 / 往期一律看 `due`。
   */
  net?: number;
  /** 消费合计（窗口内 `out` 腿）—— 与 `outflow` 同一个值（旧名保留给既有消费者） */
  consumed: number;
  /** 窗口内**退款**冲减合计（🔴 只数退款形态；批 `113` 起它是 `inflowRefund` 的旧名） */
  refunded: number;
  /** 🆕 还款优惠合计（`waiver`；批 `106-B` 起非 0） */
  waiver: number;
  /** 分期移出本金（该期无分期 ⇒ **不出键**，与 `CreditBill.installment` 同纪律） */
  installmentPrincipalDeducted?: number;
  /** 本期分期应还（同上） */
  installmentDue?: number;
  /**
   * 本期金额 = `openingBalance + outflow − inflow − waiver − 分期移出本金 + 本期分期应还`
   * （`DR-064` D1 · 替换 `AC-B2.44` 旧式）。
   * 🔴 **仅当期 / 往期有**；未出账单未出账 ⇒ 恒不出键。
   */
  due?: number;
}

/**
 * 🆕 **一张卡的某一期账单明细**（批 `106-A` · `AC-B2.27~B2.36`）。
 *
 * ## 🔴 三条硬口径
 *
 * 1. **窗口由服务端下发**（`windowStart` / `windowEnd` / `windowClosed`）——
 *    半个开口还是全闭是内核 `computeCreditBill` 与 `computeCreditBillHistory` 的事，
 *    端侧重算一次就会得出另一个答案（`AC-B2.29`）；
 * 2. **行已按「交易 × 方向」合并**（`AC-B2.37`）—— 一笔拆分在这里是**一行一个总额**，
 *    与银行流水逐行对得上；展开看子项不改变任何合计（`AC-B2.39`）；
 * 3. **`summary` 与卡面该期数字逐项一致**（`AC-B2.33`）—— 二者都由同一次
 *    `computeCreditBillHistory()` 产出，服务端不二次加工。
 */
export interface CreditBillLegsView {
  accountId: string;
  /** 账户本币（多币种：余额不换算 —— 铁律 15） */
  currency: string;
  /**
   * 期次标识：`current` / `unbilled` / 往期的**账单日** `YYYY-MM-DD`。
   * 🔴 往期一律用**账单日**（不是消费日期、也不是序号）—— 它与账单窗口一一对应。
   */
  period: string;
  /** 窗口起点（**含**） */
  windowStart: string;
  /** 窗口终点 —— `windowClosed=true` 时**含**，否则**不含** */
  windowEnd: string;
  /** `true` = 闭区间（未出账单）；`false` = 半开区间（当期 / 往期） */
  windowClosed: boolean;
  /** 该期账单日；未出账单 = 本期账单日（它自己的窗口起点） */
  billDay: string;
  /** 该期还款日；未出账单 ⇒ `null`（还没出账，没有还款日可言） */
  repaymentDate: string | null;
  /** 明细行（已合并；日期倒序） */
  rows: BillLegRowView[];
  summary: CreditBillSummaryView;
  /** 服务端实际返回的行数（已夹上限） */
  count: number;
  /** 是否被截断（`true` ⇒ 端侧提示"仅显示最近 N 行"） */
  truncated: boolean;
  /**
   * 🆕 该期的还款优惠明细（批 `106-B` · `AC-B2.50`）—— 「记优惠」列表的数据源。
   * 未出账单 / 该期无优惠 ⇒ 空数组（与分期同则：未出账不吃优惠）。
   */
  waivers: CreditWaiverView[];
}

// ═══════════════ 还款优惠与还款（批 `106-B` · B 域 PRD §5） ═══════════════

/**
 * 🆕 **一条还款优惠**（批 `106-B` · 新表 `credit_bill_waivers` 的视图）。
 *
 * 🔴 表上**不落腿 id**：腿上有 `linked_waiver_id` 反查，再存一份 = 同一事实两份载体（铁律 24）。
 */
export interface CreditWaiverView {
  id: string;
  accountId: string;
  /** 归属期账单日 `YYYY-MM-DD`（🔴 不是记账日期 —— 优惠按"哪一期账单"归属） */
  billDay: string;
  /** 整数分，> 0 */
  amount: number;
  /** 还款金 / 立减 / 活动减免 / 其他（可空） */
  kind: string | null;
  note: string | null;
  createdAt: number;
}

/** 🆕 单独记一笔优惠（账单明细页「记优惠」· `AC-B2.50`）的入参 */
export const CreateCreditWaiverSchema = z.object({
  accountId: z.string().trim().min(1),
  /** 归属期账单日（须为该卡已出账单的账单日 —— 未出账单不吃优惠，服务层校验） */
  billDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '须为账单日 YYYY-MM-DD'),
  amount: z.number().int().positive('优惠金额必须是大于 0 的整数「分」'),
  kind: z.string().trim().max(20).nullish(),
  note: z.string().trim().max(200).nullish(),
});
export type CreateCreditWaiverRequest = z.infer<typeof CreateCreditWaiverSchema>;

/** 🆕 改优惠（`AC-B2.51`：改 = 同事务改腿金额与表行） */
export const UpdateCreditWaiverSchema = z.object({
  amount: z.number().int().positive().optional(),
  kind: z.string().trim().max(20).nullish(),
  note: z.string().trim().max(200).nullish(),
});
export type UpdateCreditWaiverRequest = z.infer<typeof UpdateCreditWaiverSchema>;

/**
 * 🆕 **还款**（批 `106-B` · `POST /api/accounts/credit-bills/repay`）。
 *
 * 🔴 **单一接口原子完成**「实付转账 + 优惠落腿 + 写 waiver 表」（`AC-B2.49`）——
 * 端侧不得拆成两次 POST（批 104「分期动作非原子」的教训不重蹈）。
 */
export const CreditRepaySchema = z.object({
  /** 信用卡账户 id */
  accountId: z.string().trim().min(1),
  /** 付款方账户 id（现金 / 储蓄卡 / 电子钱包） */
  payerAccountId: z.string().trim().min(1),
  /** 还款日期（账本本地日） */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '须为 YYYY-MM-DD'),
  /** 实付金额（整数分，> 0）—— 🔴 已是**扣掉优惠后**的数（净额法） */
  amount: z.number().int().positive('还款金额必须是大于 0 的整数「分」'),
  /** 本次伴随的优惠（可空 = 无优惠） */
  waiver: z
    .object({
      amount: z.number().int().positive('优惠金额必须是大于 0 的整数「分」'),
      kind: z.string().trim().max(20).nullish(),
      note: z.string().trim().max(200).nullish(),
    })
    .optional(),
  /** 优惠归属期账单日（有 `waiver` 时必填） */
  billDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '须为账单日 YYYY-MM-DD').optional(),
});
export type CreditRepayRequest = z.infer<typeof CreditRepaySchema>;

/** 🆕 还款结果 —— 指向产物的 id（幂等回放同形） */
export interface CreditRepayResult {
  /** 还款转账交易 id */
  transactionId: string;
  /** 优惠腿交易 id（无优惠 ⇒ `null`） */
  waiverTransactionId: string | null;
  /** 优惠表行 id（无优惠 ⇒ `null`） */
  waiverId: string | null;
}

export interface AccountLedgerView {
  accountId: string;
  currency: string;
  items: AccountLegRowView[];
  /** 服务端实际返回的条数（已夹上限） */
  count: number;
  /** 是否还有更早的（`true` ⇒ 提示"只显示最近 N 条"） */
  truncated: boolean;
}

/**
 * 🆕 **投资持仓（只读）**（批次 `95`）。
 *
 * ⚠️ 投资域目前**没有服务端模块**（§PRD 未立项），本视图只是把 `holdings` 表按账户读出来，
 * 供「账户」页的投资分组展示 —— **不含买卖 / 盈亏 / 分红**（那些属投资域 PRD 范围）。
 */
export interface InvestmentHoldingView {
  id: string;
  accountId: string | null;
  /** 品种：股票 / 基金 / 债券 / 理财 / 存款 / 其他 */
  kind: string;
  code: string | null;
  name: string | null;
  /** 数量 ×10⁴ */
  quantityE4: number;
  /** 成本价（万分之一元/份） */
  costPriceE4: number;
  /** 现价（万分之一元/份） */
  currentPriceE4: number;
  currency: string;
  /** 成本额（整数分）= 数量 × 成本价 */
  costCents: number;
  /** 市值（整数分）= 数量 × 现价 */
  marketValueCents: number;
}

export interface InvestmentHoldingsResult {
  items: InvestmentHoldingView[];
}

/**
 * 🆕 贷款参数块（`type='contra'` 且 `contraKind='loan'` 时非空 —— `DR-044`）。
 *
 * 🔴 **六项全部可空**，且**可空就是可空**（不补 0、不算默认值）——
 * 「参数不全」是一个**合法状态**（用户可能只知道"还剩 200 期"）。
 * F 域的 `loan` 生成器据此判定"能否重排后续期次"（`AC-Q2.8`）。
 *
 * ⚠️ **本块只给参数，不给结果**：每期本金 / 利息 / 到期日在 `loan_schedules`
 * （`DR-044` D7 —— 防双存利率 · 铁律 24）。
 */
export interface ContraTermsView {
  dueDate: string | null;
  /** 年利率，万分之一整数（`336` = 3.36%）；`null` = 未填 */
  annualRateE4: number | null;
  /** **总期数**；`null` = 未填 */
  terms: number | null;
  startDate: string | null;
  method: RepaymentMethod | null;
  subtype: LoanSubtype | null;
}

/**
 * 账户视图（列表与详情**同一个结构** —— 账户没有"详情才看得到的东西"，
 * 拆两个类型会让两端多维护一份契约而无任何收益）。
 *
 * 🔴 `balance` 是**派生量**（腿派生），库里没有对应列（`AC-B1.4` 数据审计项）；
 * 🔴 所有展示用文案（`categoryLabel` 等）由**服务端**下发，端侧不各写一份。
 */
export interface AccountView {
  id: string;
  name: string;
  type: AccountType;
  /** `internal` 品类；其余类型为 `null` */
  category: InternalCategory | null;
  /** 品类中文名（服务端取自内核 `INTERNAL_CATEGORY_SPEC`）；非 internal 为 `null` */
  categoryLabel: string | null;
  /** `contra` 事项类型；其余类型为 `null`（🔴 与 `category` 互斥） */
  contraKind: ContraKind | null;
  /**
   * 🆕 **对手方**（跟谁）—— 仅 `type='contra'` 非空（`DR-046`）。
   * 🔴 迁移行上它**等于 `name`**（v1 把「事项」与「对手方」融在 `counterparty` 一列里）
   * ⇒ 端侧副行须**去重**（同名不重复显示），不是数据错误。
   */
  counterparty: string | null;
  /** 🔴 **归属人**（钱是谁的）—— NOT NULL、无「家庭共享」态（`DR-018`） */
  ownerId: string;
  /** 归属人展示名（`users.name`） */
  ownerName: string;
  /** ISO 4217（原币；余额永不折算 —— `DR-004`） */
  currency: string;
  /** 整数分（用户输入的事实值） */
  initialBalance: number;
  /** 🔴 **原币整数分，腿派生**（= `initialBalance + Σin − Σout`） */
  balance: number;
  /**
   * 🆕 **是否处于「锁定基准」态**（`DR-056` 方式 2 · 批次 `105`）。
   *
   * 🔴 语义与 `AdjustBalanceResult.locked` **完全一致**（同一判定：`balance_anchor_leg_id !== null`），
   * **不另起名** —— 同一个概念在"校准结果"与"账户视图"里必须是同一个词，否则端侧会写出
   * 两个名字同一含义、再各自解释一遍（铁律 24）。
   *
   * 为什么必须下发：`DR-056` 两种方式**唯一的区别**是"改锚点之前的交易会不会改变余额"，
   * 而这在界面上完全看不出来 —— 用户选了方式 2 之后，界面上没有任何提示告诉他这件事。
   */
  locked: boolean;
  /**
   * 锁定时的**锚点余额**（原币整数分）。
   *
   * 🔴 `locked = false` ⇒ **`null`**（不是 `0`）—— `0` 是一个合法的锚点余额
   * （把账户锁到 0 元），拿它当"没有锚点"就是把两种缺失混成一种（铁律 107）。
   */
  anchorAmount: number | null;
  /**
   * 🆕 **预付卡次卡**（`DR-057` D4）—— 非 `internal(prepaid)` 或未登记 ⇒ `null`。
   *
   * 🔴 `remaining` 由**服务端**算（`total − used`）：端侧自己减一遍就是第二个真值来源，
   * 而这类"总 / 已用 / 剩余"三者不一致的 bug 一旦出现，用户只会认为次数算错了。
   */
  pass: PassTimesView | null;
  icon: string | null;
  sort: number;
  hidden: boolean;
  /** 由 `category` 决定、**用户不可改**（`AC-B1.3`）；非 internal 恒 `false`（见内核说明） */
  allowNegative: boolean;
  /** 关联的周期规则 id（待缴 / 待摊才有；生成归 F 域） */
  scheduleRuleId: string | null;
  createdAt: number;
  updatedAt: number;
  /** 信用卡专属块；非信用卡为 `null` */
  credit: CreditCardView | null;
  /** 🆕 往来款项的**期限块**；非 `type='contra'` 为 `null`（`DR-044`） */
  contraTerms: ContraTermsView | null;
}

/**
 * 账户列表。
 *
 * ⚠️ **刻意不分页**（与流水列表相反）：账户是**有界小集合**
 * （v1 生产库实测 30 个；`AccountPolicy.MaxAccountsPerLedger` 兜底 200），
 * 而端侧的选择器**需要全量**（否则拼音检索只能搜到当前页）。
 * 分页反而会制造"搜不到某个账户"的 bug。
 */
export interface AccountListResult {
  items: readonly AccountView[];
  /** = `items.length`（保留字段是为了让"未隐藏/已隐藏"两段分别计数时不必再拆结构） */
  total: number;
}

/**
 * 删除结果（`DELETE /api/accounts/:id`）。
 *
 * 📌 与 `PolicyDeletedView` 同源问题：它**原本只声明在服务端**
 *    （`account.service.ts`）⇒ 端侧引用不到，只能自己再写一份 `{ deleted: true; id: string }`。
 *    **响应的形状也是契约**，不该只活在服务端（架构 §6.1 契约唯一源）。
 */
export interface AccountDeletedView {
  deleted: true;
  id: string;
}

// ═══════════════ 余额校准（`DR-056` · 批次 96） ═══════════════

/**
 * 校准方式（`DR-056` D1）—— **同一次校准只能选一种**。
 *
 * | 值 | 语义 | 改「校准之前」的交易 |
 * |---|---|---|
 * | `difference` | 只写一笔调整腿，差额参与余额派生 | **余额跟着变** |
 * | `anchor` | 写调整腿 **+** 锚点，余额从锚点起算 | **余额不变** |
 *
 * 🔴 两者**不是叠加关系**：`difference` 要求历史改动反映到余额，`anchor` 要求不反映。
 * 同时生效会让"支出变了、余额没变"且没有任何记录解释差额。
 */
export const BalanceAdjustMode = {
  Difference: 'difference',
  Anchor: 'anchor',
} as const;
export type BalanceAdjustMode = (typeof BalanceAdjustMode)[keyof typeof BalanceAdjustMode];

/** 合法的校准方式清单（服务端校验 + 端侧选项同源） */
export const BalanceAdjustModes: readonly BalanceAdjustMode[] = [
  BalanceAdjustMode.Difference,
  BalanceAdjustMode.Anchor,
];

/**
 * `POST /api/accounts/:id/adjust-balance` 请求体。
 *
 * 🔴 **只传「目标余额」，不传差额**：差额由服务端用**当前服务端余额**算
 * （端侧展示的余额可能已过期 ⇒ 让端侧算差额会把并发记账冲掉）。
 */
export const AdjustBalanceRequestSchema = z.object({
  /** 校准后账户应有的余额（原币整数分；现金类账户通常 ≥ 0，但允许负 —— 如透支的储蓄卡） */
  targetBalance: CentsSchema,
  mode: z.enum([BalanceAdjustMode.Difference, BalanceAdjustMode.Anchor], {
    message: `校准方式只能是 ${BalanceAdjustModes.join(' / ')}`,
  }),
  /**
   * 备注（写进调整腿的 `note`，便于日后对账时认出"这笔是谁调的"）。
   *
   * 🔴 **没有 `date` 字段**（刻意的）：校准是**此刻**核对现金的结果，
   * 交易日恒为服务端的账本本地日。允许补历史会与净资产口径打架 ——
   * 历史快照（`backfill`）**不认锚点**（锚点表达"此刻的事实"，在历史时间轴上没有意义），
   * 若让调整腿落在过去，曲线上就看不到那次校准的跳跃。
   */
  note: z.string().trim().max(200, '备注不超过 200 字').optional(),
});
export type AdjustBalanceRequest = z.infer<typeof AdjustBalanceRequestSchema>;

/**
 * 校准结果。
 *
 * ⚠️ `balance` 恒等于请求的 `targetBalance`（服务端**不做二次舍入**）——
 * 端侧可用它直接更新展示，不必再猜"服务端是不是又调了一下"。
 */
export interface AdjustBalanceResult {
  accountId: string;
  /** 校准前的余额（服务端算的，不是端侧传来的） */
  previousBalance: number;
  /** 校准后的余额 = `targetBalance` */
  balance: number;
  /** 差额 = `balance − previousBalance`（**正 = 余额变多**） */
  delta: number;
  mode: BalanceAdjustMode;
  /** 生成的调整腿 id（方式 1 也可凭它在流水里定位这笔） */
  legId: string;
  /** 校准后是否处于**锁定**态（`mode = anchor` ⇒ `true`；`difference` ⇒ 保持原状） */
  locked: boolean;
  /** 当前生效的锚点余额（`locked = false` ⇒ `null`） */
  anchorAmount: number | null;
}

// ═══════════════ 预付卡次卡（`DR-057` D4 · 批次 97） ═══════════════

/**
 * 账户视图里的次卡块。
 *
 * 🔴 `remaining` 是**派生量但由服务端下发**：`total − used` 的减法只写一处。
 * 端侧若自己减，一旦某天改了计价方式（例如允许"赠送次数"），两边的数立刻不一致 ——
 * 而用户只会说"次数算错了"。
 */
export interface PassTimesView {
  /** 总次数（买了多少次） */
  total: number;
  /** 单次金额（整数分） */
  unitAmount: number;
  /** 已用次数 */
  used: number;
  /** 剩余次数 = `total − used`（`0` = 用完了，与"没登记"不同） */
  remaining: number;
}

/**
 * `POST /api/accounts/:id/use-pass` —— **用一次**（`DR-057` D4.3）。
 *
 * 🔴 金额**不由端侧传**：单次金额是账户属性（`pass_unit_amount`），
 * 让端侧传就等于给"每次扣不同价"开口子，而那正是本轮明确不做的。
 */
export const UsePassRequestSchema = z.object({
  /** 分类（`null` / 省略 = 未分类）；只影响统计归属，不影响扣款金额 */
  categoryId: z.string().trim().min(1).nullish(),
  /** 备注（写进那一笔支出的 `note`） */
  note: z.string().trim().max(200).optional(),
});
export type UsePassRequest = z.infer<typeof UsePassRequestSchema>;

export interface UsePassResult {
  accountId: string;
  /** 扣款金额（= 单次金额，整数分） */
  amount: number;
  /** 动作后的已用次数 */
  usedTimes: number;
  /** 动作后的剩余次数（`0` = 刚好用完） */
  remaining: number;
  /** 生成的支出腿 id */
  legId: string;
  /** 动作后的账户余额（原币整数分） */
  balance: number;
}
